import React from "react";
import PropTypes from "prop-types";
import { Box, Grid, Paper, Stack, Typography } from "@material-ui/core";
import { formatearFecha } from "src/utils/formatTime";
import SeccionNarrativa from "./SeccionNarrativa";
import ItemsPorEmpresa from "./ItemsPorEmpresa";
import TarjetaMiembroEquipo from "./TarjetaMiembroEquipo";

const normalizarFecha = (fechaStr) => {
  if (!fechaStr) return null;
  const limpia = String(fechaStr).replace(/\//g, "-");
  if (/^\d{4}-\d{2}-\d{2}$/.test(limpia)) return limpia;
  if (/^\d{4}-\d{2}$/.test(limpia)) return `${limpia}-01`;
  return null;
};

/**
 * Columna izquierda de VerPropuestaV2 — espejo del PDF.
 * Orden estricto del template `buildProposalHTMLv2`:
 *   Portada -> Quiénes Somos -> Resumen Ejecutivo -> Antecedentes -> Alcance ->
 *   Metodología -> Equipo -> Propuesta Económica -> Próximos Pasos -> Confidencialidad.
 *
 * Cada sección es condicional: si el campo está vacío, no se renderiza.
 */
const Contenido = ({ propuesta }) => {
  const fechaNormalizada = normalizarFecha(propuesta.fechaCreacion);
  const fechaDisplay = fechaNormalizada
    ? formatearFecha(fechaNormalizada, "DD [de] MMMM [de] YYYY")
    : null;

  const equipo = Array.isArray(propuesta.equipo) ? propuesta.equipo : [];
  const equipoOrdenado = equipo
    .slice()
    .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));

  const tieneEconomica =
    propuesta.condicionesPago || propuesta.duracionEstimada;

  return (
    <Stack spacing={3}>
      {/* 1. Portada */}
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 4,
          borderRadius: 2,
          textAlign: "center",
          bgcolor: "grey.50",
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: 800, color: "text.primary", mb: 1 }}
        >
          {propuesta.titulo || "Propuesta de servicios"}
        </Typography>
        {propuesta.solicitante?.nombre && (
          <Typography variant="body1" color="textSecondary">
            Preparada para {propuesta.solicitante.nombre}
          </Typography>
        )}
        {fechaDisplay && (
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mt: 1, fontStyle: "italic" }}
          >
            {fechaDisplay}
          </Typography>
        )}
      </Paper>

      {/* 2. Quiénes Somos */}
      <SeccionNarrativa titulo="Quiénes Somos" value={propuesta.introduccion} />

      {/* 3. Resumen Ejecutivo */}
      <SeccionNarrativa
        titulo="Resumen Ejecutivo"
        value={propuesta.resumenEjecutivo}
      />

      {/* 4. Antecedentes */}
      {(propuesta.antecedentes || propuesta.descripcion) && (
        <SeccionNarrativa
          titulo="Antecedentes"
          value={propuesta.antecedentes}
          descripcionItalic={propuesta.descripcion}
        />
      )}

      {/* 5. Alcance */}
      <SeccionNarrativa
        titulo="Alcance del Servicio"
        value={propuesta.alcance}
      />

      {/* 6. Metodología / Ítems */}
      <ItemsPorEmpresa items={propuesta.items ?? []} />

      {/* 7. Equipo de Trabajo */}
      {equipoOrdenado.length > 0 && (
        <SeccionNarrativa titulo="Equipo de Trabajo">
          <Grid container spacing={2}>
            {equipoOrdenado.map((m) => (
              <Grid item xs={12} md={6} key={m.id ?? m.orden}>
                <TarjetaMiembroEquipo miembro={m} />
              </Grid>
            ))}
          </Grid>
        </SeccionNarrativa>
      )}

      {/* 8. Propuesta Económica (condiciones pago + duración) */}
      {tieneEconomica && (
        <SeccionNarrativa titulo="Propuesta Económica">
          <Stack spacing={2}>
            {propuesta.condicionesPago && (
              <Box>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: 700 }}
                >
                  Condiciones de pago
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}
                >
                  {propuesta.condicionesPago}
                </Typography>
              </Box>
            )}
            {propuesta.duracionEstimada && (
              <Box>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: 700 }}
                >
                  Duración estimada
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {propuesta.duracionEstimada}
                </Typography>
              </Box>
            )}
          </Stack>
        </SeccionNarrativa>
      )}

      {/* 9. Próximos Pasos */}
      <SeccionNarrativa
        titulo="Próximos Pasos"
        value={propuesta.proximosPasos}
      />

      {/* 10. Confidencialidad */}
      <SeccionNarrativa
        titulo="Confidencialidad"
        value={propuesta.clausulaConfidencialidad}
      />
    </Stack>
  );
};

Contenido.propTypes = {
  propuesta: PropTypes.object.isRequired,
};

export default Contenido;
